import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
  SIGNATURE_COMPONENT_DEFAULT_WIDTH,
  SignatureComponentType,
} from 'src/constants';
import { useContractDateFormat } from 'src/hooks/useContractDateFormat';
import { CANVAS_SIGNATURE_COMPONENT_HEIGHT } from 'src/legacy/components/Canvas/BaseCanvas';
import { RootState } from 'src/store';
import {
  CompleteReceiverComponentAction,
  InsertEverywhereAction,
  RemoveActiveComponent,
  SetActiveComponent,
  SkipComponentAction,
  UpdateClientSignatureFlowStatus,
} from 'src/store/signaturePage/actions';
import {
  ESignatureFlowStatus,
  SignaturePageComponent,
} from 'src/store/signaturePage/types';

import { useAddDateForm } from '../../UI/Date/useAddDateForm';
import { scrollToComponent } from '../ClientSignatureIndicatorPanel';
import { SignatureClientFormV2 } from '../SignatureComponents/SignatureClientFormV2';

import { useAddCheckboxClientForm } from './useAddCheckboxClientForm';
import { useAddMySignatureForm } from './useAddMySignatureForm';
import { useAddTextForm } from './useAddTextForm';

type SelectedComponentObject = {
  component: SignaturePageComponent | null;
  element?: HTMLButtonElement | null;
};

type PrimaryClickAction = {
  formValues: any;
  selectedComponent: SelectedComponentObject;
  callbackFunction: (param: SelectedComponentObject | null) => void;
};

export const useSignatureRequestComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dateFormat = useContractDateFormat();
  const pageComponents = useSelector(
    (state: RootState) => state.signaturePage.pageComponents,
  );

  const activeComponentKey = useSelector(
    (state: RootState) => state.signaturePage.activeComponentKey,
  );

  const [selectedReceiverSignComponent, setSelectedReceiverSignComponent] =
    React.useState<SelectedComponentObject | null>(null);
  const [selectedReceiverDateComponent, setSelectedReceiverDateComponent] =
    React.useState<SelectedComponentObject | null>(null);
  const [
    selectedReceiverInitialsComponent,
    setSelectedReceiverInitialsComponent,
  ] = React.useState<SelectedComponentObject | null>(null);
  const [selectedReceiverTextComponent, setSelectedReceiverTextComponent] =
    React.useState<SelectedComponentObject | null>(null);
  const [
    selectedReceiverCheckboxComponent,
    setSelectedReceiverCheckboxComponent,
  ] = React.useState<SelectedComponentObject | null>(null);

  // if true find the next component to be signed
  const [getNextComponentToSign, setGetNextComponentToSign] =
    React.useState(false);

  // State to track the signature input types whose values are filled using insert everywhere.
  // This helps in skipping these inputs during the "Back to Edit" flow.
  const [filledByInsertEverywhere, setFilledByInsertEverywhere] = useState<
    string[]
  >([]);

  const resetAllFormStates = () => {
    setSelectedReceiverDateComponent(null);
    setSelectedReceiverInitialsComponent(null);
    setSelectedReceiverSignComponent(null);
    setSelectedReceiverTextComponent(null);
    setSelectedReceiverCheckboxComponent(null);
  };

  /**
   * handleRequestReceiverInfo - hook method that receives the eSignature (eSig.) component
   * regarding which component type is. Before changing the active client form we need to clean the previous state for the cases, when two
   * equal types of components are in the eSig document. If not clean then state will have the previous component
   * and client form will not show
   * @param component eSignature component
   */
  const handleRequestReceiverInfo = async (
    component: SignaturePageComponent,
  ) => {
    const selectedComponent = {
      component,
    };

    resetAllFormStates();

    switch (component.componentType) {
      case SignatureComponentType.REQUEST_SIGN:
        setSelectedReceiverSignComponent(selectedComponent);
        break;
      case SignatureComponentType.REQUEST_DATE:
        setSelectedReceiverDateComponent(selectedComponent);
        break;
      case SignatureComponentType.REQUEST_INITIAL:
        setSelectedReceiverInitialsComponent(selectedComponent);
        break;
      case SignatureComponentType.REQUEST_TEXT:
        setSelectedReceiverTextComponent(selectedComponent);
        break;
      case SignatureComponentType.REQUEST_CHECKBOX:
        setSelectedReceiverCheckboxComponent(selectedComponent);
        break;
      default:
        console.info('request component', component);
    }
  };

  const handleRemoveActiveComponent = async (
    callbackFunction: (param: SelectedComponentObject | null) => void,
  ) => {
    callbackFunction(null);
  };

  const getResizedDimensionsForSignatures = (
    component: Required<SignaturePageComponent & { fields: any }>,
  ): SignaturePageComponent => {
    const {
      height: placeHolderComponentHeight,
      width: placeHolderComponentWidth,
      fields: { signatureWidth },
    } = component;
    let width: number | '100%';
    let height: number;

    // Documents sent for e-sigs in V1 have width,height = 0. This causes problem for e-signatures in V2.
    // Setting them to default component width and height for correct calculations.
    const componentHeight =
      placeHolderComponentHeight || SIGNATURE_COMPONENT_DEFAULT_HEIGHT;
    const componentWidth =
      placeHolderComponentWidth || SIGNATURE_COMPONENT_DEFAULT_WIDTH;

    const isWidthGreaterThanHeight = componentWidth > componentHeight;

    const placeHolderToImageHeightRatio =
      componentHeight / CANVAS_SIGNATURE_COMPONENT_HEIGHT;

    const placeHolderToImageWidthRatio = componentWidth / signatureWidth;

    const resizedWidth = signatureWidth * placeHolderToImageHeightRatio;

    const resizedHeight =
      CANVAS_SIGNATURE_COMPONENT_HEIGHT * placeHolderToImageWidthRatio;

    if (isWidthGreaterThanHeight) {
      // If width of placeholder component is greater than height, there are two edge cases to be handled:
      // If text is longer than placeholder width, we want image to be stretched.
      // Else component needs to be constrained by height and to be left aligned so that it doesn't stretched to very end.
      if (signatureWidth > componentWidth) {
        width = componentWidth;
        height = componentHeight;
      } else {
        width = resizedWidth;
        height = componentHeight;
      }
    } else {
      // If height of placeholder component is greater than width, component needs to be aligned to center vertically and edge to edge horizontally.
      width = componentWidth;
      height = resizedHeight;
    }
    return {
      ...component,
      resizedHeight: Math.floor(height),
      resizedWidth: Math.floor(width),
    };
  };

  // get all the client components in visual reading order: page first, then
  // y-position within the page. A pure-yPosition sort scrambles multi-page
  // documents because each page has its own y-coordinate space.
  const clientComponents = React.useMemo(
    () =>
      pageComponents
        ?.filter((component) => component.componentType.startsWith('request'))
        .sort(
          (a, b) => (a.page ?? 0) - (b.page ?? 0) || a.yPosition - b.yPosition,
        ) || [],
    [pageComponents],
  );

  /**
   * Method to find the next client signature component that needs to be signed
   * and make it active.
   *
   * Scans forward from the current index so the wizard never bounces backward
   * (which would happen with a global "first unfilled" lookup if a value got
   * cleared mid-flow). Falls back to a global scan only when no forward field
   * is unfilled, and finally to END_SIGNAURE.
   */
  const findNextSigningComponent = () => {
    const currentActiveComponentIndex = clientComponents.findIndex(
      (component) => component.key === activeComponentKey,
    );

    const isFillable = (component: SignaturePageComponent) => {
      if (component.isSkipped) return false;
      if (component.key === activeComponentKey) return false;
      // REQUEST_CHECKBOX values are deterministic 'true'/'false' strings
      // (seeded on drop and on contract hydration), so `!component.value`
      // is falsy for the initial 'false' state. The wizard should re-land
      // on any checkbox whose value isn't 'true'; the isSkipped guard above
      // prevents an optional checkbox from re-appearing after the CU has
      // explicitly skipped it.
      if (component.componentType === SignatureComponentType.REQUEST_CHECKBOX) {
        return component.value !== 'true';
      }
      return !component.value;
    };

    const matchesInsertEverywhere = (component: SignaturePageComponent) =>
      filledByInsertEverywhere.length === 0 ||
      !filledByInsertEverywhere.includes(component.componentType);

    // 1. Next unfilled component AFTER the current one (preserves visual order).
    const forwardUnfilled = clientComponents
      .slice(Math.max(currentActiveComponentIndex + 1, 0))
      .find((c) => isFillable(c) && matchesInsertEverywhere(c));

    if (forwardUnfilled) {
      dispatch(SetActiveComponent(forwardUnfilled.key));
      setGetNextComponentToSign(false);
      return;
    }

    // 2. Fallback: any unfilled component (handles back-to-edit where an
    //    earlier field was un-filled or re-opened).
    const anyUnfilled = clientComponents.find(
      (c) => isFillable(c) && matchesInsertEverywhere(c),
    );

    if (anyUnfilled) {
      dispatch(SetActiveComponent(anyUnfilled.key));
      setGetNextComponentToSign(false);
      return;
    }

    // 3. Nothing left to fill — move the user to the End-Signature step.
    dispatch(RemoveActiveComponent());
    dispatch(
      UpdateClientSignatureFlowStatus(ESignatureFlowStatus.END_SIGNAURE),
    );
    setGetNextComponentToSign(false);
    setFilledByInsertEverywhere([]);
  };

  React.useEffect(() => {
    if (getNextComponentToSign) {
      findNextSigningComponent();
    }
  }, [getNextComponentToSign, clientComponents]);

  React.useEffect(() => {
    // if there is no active component do not return any client signature form
    if (!activeComponentKey) {
      resetAllFormStates();
      return;
    }

    // based on activeComponentKey set client signature form
    const stageComponent = clientComponents.find(
      (c) => c.key === activeComponentKey,
    );

    if (stageComponent) {
      scrollToComponent(stageComponent.key);
      handleRequestReceiverInfo(stageComponent);
    }
  }, [activeComponentKey]);

  const handleSaveComponent = async (
    formValues: any,
    callbackFunction: (param: SelectedComponentObject | null) => void,
  ) => {
    let payload: any = {};
    switch (formValues.componentType) {
      case SignatureComponentType.REQUEST_SIGN:
      case SignatureComponentType.REQUEST_INITIAL:
        payload = getResizedDimensionsForSignatures(formValues);
        break;
      default:
        payload = { ...formValues };
        break;
    }
    // Optional checkbox committed via Insert without being checked: mark it
    // skipped so the back-to-edit fallback scan does not loop the CU back
    // onto a field they already deliberately left unchecked. The isSkipped
    // flag is frontend-only and is not part of the API payload.
    if (
      payload.componentType === SignatureComponentType.REQUEST_CHECKBOX &&
      payload.isOptional &&
      payload.value !== 'true'
    ) {
      payload.isSkipped = true;
    }
    dispatch(CompleteReceiverComponentAction(payload));
    handleRemoveActiveComponent(callbackFunction);
    setGetNextComponentToSign(true);
  };

  const handleInsertEverywhere = async (
    formValues: any,
    callbackFunction: (param: SelectedComponentObject | null) => void,
  ) => {
    let payload: any = {};
    switch (formValues.componentType) {
      case SignatureComponentType.REQUEST_SIGN:
      case SignatureComponentType.REQUEST_INITIAL:
        payload = getResizedDimensionsForSignatures(formValues);
        break;
      default:
        payload = { ...formValues };
        break;
    }
    dispatch(InsertEverywhereAction(payload));
    handleRemoveActiveComponent(callbackFunction);
    setFilledByInsertEverywhere((prev) => [...prev, formValues.componentType]);
    setGetNextComponentToSign(true);
  };
  const handleSkipComponent = async (
    formValues: any,
    callbackFunction: (param: SelectedComponentObject | null) => void,
  ) => {
    dispatch(SkipComponentAction(formValues));
    handleRemoveActiveComponent(callbackFunction);
    setGetNextComponentToSign(true);
  };

  // This function finds all the request component for a specific type
  // return true if we have >= 2 components of same request type
  const hasMultipleComponentsByType = (
    requestComponentType: string,
  ): boolean => {
    const components = pageComponents.filter(
      (c) => c.componentType === requestComponentType,
    );
    return components.length >= 2;
  };

  const getPrimaryButtonLabel = (
    selectedComponent: SelectedComponentObject,
  ) => {
    if (!selectedComponent.component) return '';

    // show 'Insert everywhere' label only when we have >= 2 request components of same type
    if (
      !hasMultipleComponentsByType(selectedComponent.component?.componentType)
    )
      return 'Insert';

    return 'Insert everywhere';
  };
  const getSecondaryButtonLabel = (
    selectedComponent: SelectedComponentObject,
  ) => {
    if (!selectedComponent?.component) return '';

    // show secondary button only when we have >= 2 request components of same type
    // since we will be using primary button to insert
    if (!hasMultipleComponentsByType(selectedComponent.component.componentType))
      return '';

    return 'Insert';
  };

  const handlePrimaryButtonClick = ({
    formValues,
    selectedComponent,
    callbackFunction,
  }: PrimaryClickAction) => {
    if (!selectedComponent?.component) return;

    // When we have less that 2 request components of same type
    if (
      !hasMultipleComponentsByType(selectedComponent.component?.componentType)
    ) {
      handleSaveComponent(formValues, callbackFunction);
      return;
    }
    // When insert everywhere is clicked
    handleInsertEverywhere(formValues, callbackFunction);
  };

  const textComponentFormHook = useAddTextForm();
  const checkboxClientFormHook = useAddCheckboxClientForm();

  const signatureComponentsForm = () => {
    // Show the form to add the signature for Client
    if (selectedReceiverSignComponent?.component)
      return (
        <SignatureClientFormV2
          initialFormValue={{
            name: '',
            isAgree: false,
            ...(selectedReceiverSignComponent?.component || {}),
            value: selectedReceiverSignComponent?.component?.imageTextValue,
          }}
          title={t(($) => $.page_contracts.inputs.signature.cu_label)}
          primaryButtonActionLabel={getPrimaryButtonLabel(
            selectedReceiverSignComponent,
          )}
          secondaryButtonActionLabel={getSecondaryButtonLabel(
            selectedReceiverSignComponent,
          )}
          onPrimaryButtonClick={(formValues: any) =>
            handlePrimaryButtonClick({
              formValues,
              selectedComponent: selectedReceiverSignComponent,
              callbackFunction: setSelectedReceiverSignComponent,
            })
          }
          onSecondaryButtonClick={(formValues: any) =>
            handleSaveComponent(formValues, setSelectedReceiverSignComponent)
          }
          useFormHook={() =>
            useAddMySignatureForm(
              'signature',
              selectedReceiverSignComponent?.component?.imageTextValue,
            )
          }
        />
      );
    // Show the form to add the date for Client
    if (selectedReceiverDateComponent?.component)
      return (
        <SignatureClientFormV2
          initialFormValue={{
            ...(selectedReceiverDateComponent?.component || {}),
            value:
              selectedReceiverDateComponent?.component.value ||
              moment().format(dateFormat),
          }}
          title={t(($) => $.page_contracts.inputs.date.cu_label)}
          primaryButtonActionLabel={getPrimaryButtonLabel(
            selectedReceiverDateComponent,
          )}
          secondaryButtonActionLabel={getSecondaryButtonLabel(
            selectedReceiverDateComponent,
          )}
          onPrimaryButtonClick={(formValues: any) =>
            handlePrimaryButtonClick({
              formValues,
              selectedComponent: selectedReceiverDateComponent,
              callbackFunction: setSelectedReceiverDateComponent,
            })
          }
          onSecondaryButtonClick={(formValues: any) =>
            handleSaveComponent(formValues, setSelectedReceiverDateComponent)
          }
          useFormHook={useAddDateForm}
        />
      );
    // Show the form to add the initials for Client
    if (selectedReceiverInitialsComponent?.component) {
      return (
        <SignatureClientFormV2
          initialFormValue={{
            name: '',
            ...(selectedReceiverInitialsComponent?.component || {}),
            value: selectedReceiverInitialsComponent?.component?.imageTextValue,
          }}
          title={t(($) => $.page_contracts.inputs.initials.cu_label)}
          primaryButtonActionLabel={getPrimaryButtonLabel(
            selectedReceiverInitialsComponent,
          )}
          secondaryButtonActionLabel={getSecondaryButtonLabel(
            selectedReceiverInitialsComponent,
          )}
          onPrimaryButtonClick={(formValues: any) =>
            handlePrimaryButtonClick({
              formValues,
              selectedComponent: selectedReceiverInitialsComponent,
              callbackFunction: setSelectedReceiverInitialsComponent,
            })
          }
          onSecondaryButtonClick={(formValues: any) =>
            handleSaveComponent(
              formValues,
              setSelectedReceiverInitialsComponent,
            )
          }
          useFormHook={() =>
            useAddMySignatureForm(
              'initial',
              selectedReceiverInitialsComponent?.component?.imageTextValue,
            )
          }
        />
      );
    }
    // Show the form to add text for Client
    if (selectedReceiverTextComponent?.component) {
      return (
        <SignatureClientFormV2
          initialFormValue={{
            ...(selectedReceiverTextComponent?.component || {}),
          }}
          title={
            selectedReceiverTextComponent?.component?.label ??
            t(($) => $.page_contracts.inputs.textfield.cu_label)
          }
          primaryButtonActionLabel="Insert"
          secondaryButtonActionLabel={
            selectedReceiverTextComponent?.component?.isOptional ? 'Skip' : ''
          }
          onPrimaryButtonClick={(formValues: any) =>
            handleSaveComponent(formValues, setSelectedReceiverTextComponent)
          }
          onSecondaryButtonClick={(formValues: any) =>
            handleSkipComponent(formValues, setSelectedReceiverTextComponent)
          }
          useFormHook={() => textComponentFormHook}
          isOptional={selectedReceiverTextComponent?.component?.isOptional}
        />
      );
    }
    // Show the form for Client checkbox. Doc-side clicks update the redux
    // value (via SignatureDraggableComponent); this form mirrors that value
    // and is what commits + advances when the CU clicks Insert.
    //
    // Gate the form on selectedReceiverCheckboxComponent only (the same
    // pattern every other field type uses) and look the component up by its
    // snapshot key. The canvas toggle path updates Redux directly, so we
    // still read clientComponents to get the fresh value, but the gate no
    // longer depends on activeComponentKey arriving before the snapshot.
    const liveActiveCheckbox = selectedReceiverCheckboxComponent?.component
      ? clientComponents.find(
          (c) =>
            c.key === selectedReceiverCheckboxComponent.component!.key &&
            c.componentType === SignatureComponentType.REQUEST_CHECKBOX,
        )
      : undefined;
    if (liveActiveCheckbox) {
      return (
        <SignatureClientFormV2
          initialFormValue={{
            ...liveActiveCheckbox,
            // Default to 'false' so the form always sends a deterministic
            // value (the BE stamps an empty box when value !== 'true').
            value: liveActiveCheckbox.value === 'true' ? 'true' : 'false',
          }}
          title={t(($) => $.page_contracts.inputs.checkbox.cu_label)}
          primaryButtonActionLabel="Insert"
          secondaryButtonActionLabel={
            liveActiveCheckbox.isOptional ? 'Skip' : ''
          }
          onPrimaryButtonClick={(formValues: any) =>
            handleSaveComponent(
              formValues,
              setSelectedReceiverCheckboxComponent,
            )
          }
          onSecondaryButtonClick={(formValues: any) =>
            handleSkipComponent(
              formValues,
              setSelectedReceiverCheckboxComponent,
            )
          }
          useFormHook={() => checkboxClientFormHook}
          isOptional={liveActiveCheckbox.isOptional}
        />
      );
    }
    return null;
  };

  const SignatureComponentsForm = () => signatureComponentsForm();

  return {
    handleRequestReceiverInfo,
    SignatureComponentsForm,
  };
};
